import { useAccountSetting } from "@/settings/account-settings-context";
import { type DensityFilters, defaultFilters, MARKET_ORDER } from "./derive";
import { FILTERABLE_STATES } from "./stream-state";

/**
 * Фильтры экрана §9 (§70 п. 1–3). С §127 живут в настройках аккаунта; чтение
 * из браузера (`readDensityFilters`) осталось для переезда прежних записей
 * (design.md stage9-account-settings, Д5). Сортировка и раскрытие панели не
 * запоминаются (§70 п. 2).
 */

export const DENSITY_FILTERS_STORAGE_KEY = "screener.density.filters";

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readMinUsd(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) && value >= 0
    ? value
    : defaultFilters.minUsd;
}

export function readStates(value: unknown): DensityFilters["states"] {
  if (!Array.isArray(value)) {
    return defaultFilters.states;
  }
  // Только стадии, у которых есть фишка: иначе восстановленный отбор нечем снять.
  return FILTERABLE_STATES.filter((state) => value.includes(state)).sort(
    (left, right) => value.indexOf(left) - value.indexOf(right),
  );
}

/**
 * Отбор бирж хранится ключами «<биржа>:<рынок>» (§115 п. 5). Элемент без «:» —
 * прежний формат (до 1.102): биржа получает все свои рынки (§115 п. 7).
 */
export function readExchanges(value: unknown): string[] | null {
  if (Array.isArray(value) && value.every((item) => typeof item === "string")) {
    return value.flatMap((item) =>
      item.includes(":") ? [item] : MARKET_ORDER.map((market) => `${item}:${market}`),
    );
  }
  return defaultFilters.exchanges;
}

export function readFlag(value: unknown, fallback: boolean): boolean {
  return typeof value === "boolean" ? value : fallback;
}

/**
 * Читает фильтры из хранилища, поле за полем: поле, не прошедшее проверку,
 * берёт значение по умолчанию, остальные сохраняются. Поле `showPending`
 * из записей прежней версии игнорируется (§69).
 */
export function readDensityFilters(storage: Storage): DensityFilters {
  try {
    const raw = storage.getItem(DENSITY_FILTERS_STORAGE_KEY);
    if (raw === null) {
      return defaultFilters;
    }
    const parsed: unknown = JSON.parse(raw);
    if (!isRecord(parsed)) {
      return defaultFilters;
    }
    return {
      minUsd: readMinUsd(parsed.minUsd),
      states: readStates(parsed.states),
      exchanges: readExchanges(parsed.exchanges),
      hideMm: readFlag(parsed.hideMm, defaultFilters.hideMm),
      onlyFavorites: readFlag(parsed.onlyFavorites, defaultFilters.onlyFavorites),
    };
  } catch {
    return defaultFilters;
  }
}

/**
 * Фильтры экрана берутся из настроек аккаунта (§127), а не из браузера;
 * подпись прежняя. Запись идёт в документ и уходит в аккаунт не чаще раза в
 * секунду. Сеттер — постоянной ссылкой: мемоизированная полоса фильтров не
 * перерисовывается из-за новой функции на каждый рендер экрана.
 */
export function useDensityFilters(): [DensityFilters, (next: DensityFilters) => void] {
  const [store, filters] = useAccountSetting("filters");
  return [filters, store.setFilters];
}
